/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Link,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import TranslateIcon from '@mui/icons-material/Translate';
import { LoadingButton } from '@mui/lab';
import { Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { TranslationState } from '@ink-ai/portal/reducers/translation';
import { useDispatch } from 'react-redux';

interface UploadItem {
  name: string;
  size: number;
  type: string;
  status: 'waiting upload' | 'queueing' | 'uploaded' | 'upload failed' | 'unsupported';
  progress: number;
  selected: boolean;
  uuid?: string;
  reason?: string;
}

interface FileUploadAreaProps {
  translation: TranslationState;
  items: UploadItem[];
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFolderSelectChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleTranslate: () => void;
  handleRemoveItem: (index: number) => void;
  handleToggleSelect: (index: number, selected: boolean) => void;
  handleClearAll: () => void;
  formatFileSize: (size: number) => string;
}

export const DocumentTranslateArea: React.FC<FileUploadAreaProps> = ({
  translation,
  items,
  handleDrop,
  handleDragOver,
  handleFileChange,
  handleFolderSelectChange,
  handleTranslate,
  handleRemoveItem,
  handleToggleSelect,
  handleClearAll,
  formatFileSize,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const handleFormatDialogOpen = () => {
    setFormatDialogOpen(true);
  };

  const handleFormatDialogClose = () => {
    setFormatDialogOpen(false);
  };
  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(event);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const onFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFolderSelectChange(event);
    if (folderInputRef.current) folderInputRef.current.value = '';
  };
  const onClearAll = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
    handleClearAll();
  };
  const disabledStatuses = new Set<UploadItem['status']>(['uploaded', 'upload failed', 'unsupported']);
  const selectableItems = items.filter((it) => !disabledStatuses.has(it.status));
  const allSelected = selectableItems.length > 0 && selectableItems.every((it) => it.selected);
  const someSelected = selectableItems.some((it) => it.selected);
  const selectedCount = items.filter((it) => it.selected && (it.status === 'waiting upload' || it.status === 'queueing')).length;
  const toggleAll = (checked: boolean) => {
    items.forEach((it, idx) => {
      if (!disabledStatuses.has(it.status)) handleToggleSelect(idx, checked);
    });
  };
  const renderFileType = (name: string, mime?: string) => {
    const ext = (name.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'docx':
        return t('translation.fileType.word');
      case 'pdf':
        return t('translation.fileType.pdf');
      case 'json':
        return t('translation.fileType.json');
      case 'txt':
        return t('translation.fileType.text');
      case 'pptx':
        return t('translation.fileType.ppt');
      case 'xlsx':
        return t('translation.fileType.excel');
      case 'rtf':
        return t('translation.fileType.rtf');
      case 'md':
      case 'markdown':
        return t('translation.fileType.markdown');
      case 'bmp':
      case 'jpg':
      case 'jpeg':
      case 'png':
        return t('translation.fileType.image');
      default:
        if ((mime || '').includes('pdf')) return t('translation.fileType.pdf');
        return (ext || (mime || '')).toUpperCase();
    }
  };
  const renderStatusLabel = (status: UploadItem['status']) => {
    if (status === 'waiting upload') return t('translation.status.pendingUpload');
    if (status === 'queueing') return t('translation.status.uploading');
    if (status === 'uploaded') return t('translation.status.uploaded');
    if (status === 'upload failed') return t('translation.status.uploadFailed');
    if (status === 'unsupported') return t('translation.status.unsupported');
    return status;
  };
  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: items.length > 0 ? 'none' : '2px dashed #ccc',
        borderRadius: '4px',
        padding: '20px',
        textAlign: 'center',
        width: '100%',
        margin: '2rem auto',
      }}
    >
      <input
        accept={translation.translationMode === 'SUBMISSION'
          ? '.pdf, .docx, .xml'
          : '.docx, .pdf, .json, .zip, .tgz, .tar, .7z, .txt, .pptx, .xlsx, .rtf, .md, .markdown, .bmp, .jpg, .jpeg, .png, .html, .htm, .xhtml'}
        multiple
        className="hidden"
        id="upload-file"
        type="file"
        onChange={onFileChange}
        ref={fileInputRef}
      />
      <input
        className="hidden"
        id="upload-folder"
        type="file"
        {...({ webkitdirectory: '' } as any)}
        onChange={onFolderChange}
        ref={folderInputRef}
      />
      {items.length === 0 ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <UploadFileIcon sx={{ fontSize: '24px', color: '#2196F3' }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <label htmlFor="upload-file" style={{ cursor: 'pointer' }}>
              <Link component="span" underline="hover">
                {t('translation.document.clickToUpload')}
              </Link>
            </label>
            <Typography component="span">|</Typography>
            <label htmlFor="upload-folder" style={{ cursor: 'pointer' }}>
              <Link component="span" underline="hover">
                {t('translation.document.selectFolder', 'Select Folder')}
              </Link>
            </label>
            &nbsp;{t('translation.document.dragAndDrop')}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            {t('translation.document.fileSizeAndFormat')}
            <Link 
              component="span" 
              underline="hover" 
              sx={{ cursor: 'pointer' }}
              onClick={handleFormatDialogOpen}
            >
              {t('translation.document.supportedFormats')}
            </Link>
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#eeeeee',
              padding: '8px 10px',
              borderRadius: '4px',
              mb: 1,
            }}
          >
            <Box sx={{ width: 40, textAlign: 'left' }}>
              <Checkbox
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                onChange={(e) => toggleAll(e.target.checked)}
              />
            </Box>
            <Box sx={{ flex: 4, textAlign: 'left' }}>
              <Typography variant="body2" color="text.secondary">
                {t('table.name')}
              </Typography>
            </Box>
            <Box sx={{ width: 100, textAlign: 'left', flex: '0 0 100px' }}>
              <Typography variant="body2" color="text.secondary">
                {t('table.size')}
              </Typography>
            </Box>
            <Box sx={{ width: 110, textAlign: 'left', flex: '0 0 110px' }}>
              <Typography variant="body2" color="text.secondary">
                {t('table.type')}
              </Typography>
            </Box>
            <Box sx={{ width: 150, textAlign: 'left', flex: '0 0 150px' }}>
              <Typography variant="body2" color="text.secondary">
                {t('table.status')}
              </Typography>
            </Box>
            {/* <Box sx={{ width: 60, textAlign: 'left', flex: '0 0 60px' }}>
              <Typography variant="body2" color="text.secondary">
                {t('table.actions')}
              </Typography>
            </Box> */}
          </Box>
          {items.map((item, idx) => (
            <Box
              key={item.name + idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px',
                mb: 1,
              }}
            >
              <Box sx={{ width: 40 }}>
                <Checkbox
                  checked={item.selected}
                  onChange={(e) => handleToggleSelect(idx, e.target.checked)}
                  disabled={disabledStatuses.has(item.status)}
                />
              </Box>
              <Box sx={{ flex: 4, display: 'flex', alignItems: 'center', textAlign: 'left' }}>
                <InsertDriveFileIcon sx={{ mr: 1, color: '#2196F3' }} />
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{item.name}</Typography>
              </Box>
              <Box sx={{ width: 100, flex: '0 0 100px', textAlign: 'left' }}>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(item.size)}
                </Typography>
              </Box>
              <Box sx={{ width: 110, flex: '0 0 110px', textAlign: 'left' }}>
                <Typography variant="body2" color="text.secondary">
                  {renderFileType(item.name, item.type)}
                </Typography>
              </Box>
              <Box sx={{ width: 150, flex: '0 0 150px', textAlign: 'left' }}>
                {item.status === 'uploaded' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleOutlineIcon sx={{ color: 'success.main', mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      {renderStatusLabel(item.status)}
                    </Typography>
                  </Box>
                ) : item.status === 'upload failed' ? (
                  <Tooltip title={item.reason || ''} arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <HighlightOffIcon sx={{ color: 'error.main', mr: 0.5 }} />
                      <Typography variant="body2" sx={{ color: 'error.main' }}>
                        {renderStatusLabel(item.status)}
                      </Typography>
                    </Box>
                  </Tooltip>
                ) : item.status === 'unsupported' ? (
                  <Tooltip title={item.reason || ''} arrow>
                    <Typography variant="body2" color="text.secondary">
                      {renderStatusLabel(item.status)}
                    </Typography>
                  </Tooltip>
                ) : item.status === 'queueing' ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {renderStatusLabel(item.status)}
                    </Typography>
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <LinearProgress />
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {renderStatusLabel(item.status)}
                  </Typography>
                )}
              </Box>
              {/* <Box sx={{ width: 60, display: 'flex', justifyContent: 'flex-start', flex: '0 0 60px' }}>
                <IconButton onClick={() => handleRemoveItem(idx)}>
                  <DeleteIcon />
                </IconButton>
              </Box> */}
            </Box>
          ))}
        </Box>
      )}
      {items.length > 0 && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Button variant="text" color="secondary" onClick={onClearAll} disabled={items.length === 0}>
              {t('translation.document.clearList', '清空列表')}
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* <Typography variant="body2" sx={{ color: selectedCount > 10 ? 'error.main' : 'text.secondary' }}>
                {`${selectedCount}/10`}
              </Typography> */}
              <LoadingButton
                variant="contained"
                loadingPosition="start"
                color="primary"
                sx={{ textTransform: 'none' }}
                onClick={handleTranslate}
                loading={translation.loading}
                startIcon={<TranslateIcon />}
              >
                {translation.loading
                  ? t('translation.document.translating')
                  : t('translation.document.translate')}
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      )}
      
      {/* Format Support Dialog */}
      <Dialog 
        open={formatDialogOpen} 
        onClose={handleFormatDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('translation.document.formatDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {t('translation.document.formatDialog.basicFormats')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('translation.document.formatDialog.limitations')}
          </Typography>
          {/* <Typography variant="body1" paragraph>
            {t('translation.document.formatDialog.svipUpgrade')}
          </Typography> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFormatDialogClose} color="primary">
            {t('translation.document.formatDialog.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
